import useAuthQuery from "globalQueries/useAuthQuery";
import BaseApiService from "helper/apiService/BaseApiService";
import {
  AppStorageKey,
  ReferrerCodes,
  localReferralExpiryKey,
  localReferralKey,
} from "helper/constant";
import { logger } from "helper/debug/logger";
import { useCallback, useEffect, useState } from "react";

const useReferrer = () => {
  const [referrerCode, setReferrerCode] = useState(null);
  const [isKakaoAvailable, setIsKakaoAvailable] = useState(true);
  const [isPassAvailable, setIsPassAvailable] = useState(true);
  const [isTossAvailable, setIsTossAvailable] = useState(true);
  const [isGeneralReferrer, setIsGeneralReferrer] = useState(true);
  const { userInfo } = useAuthQuery();

  const checkReferrer = useCallback(async () => {
    // userInfo 에서 referrer_code 를 체크
    // 회원가입 후 localStorage 에 저장된 referrer_code 는 삭제되므로 userInfo에서 확인해야 함
    if (userInfo?.referrer_code) {
      if (isKtLguReferrerCode(userInfo?.referrer_code)) {
        setIsKakaoAvailable(false);
        setIsPassAvailable(true);
        setIsTossAvailable(false);
        setIsGeneralReferrer(false);
      } else if (checkUserInfoReferrerCode(userInfo?.referrer_code)) {
        setIsKakaoAvailable(true);
        setIsPassAvailable(true);
        setIsTossAvailable(true);
        setIsGeneralReferrer(true);
      } else {
        setIsKakaoAvailable(true);
        setIsPassAvailable(true);
        setIsTossAvailable(true);
        setIsGeneralReferrer(true);
      }
      setReferrerCode(userInfo?.referrer_code);
      return;
    }
    // 회원가입 전이면 userInfo 에 값이 없으므로 localStorage 값을 확인
    if (checkReferralCode(ReferrerCodes.KtPass)) {
      setIsKakaoAvailable(false);
      setIsPassAvailable(true);
      setIsTossAvailable(false);
      setIsGeneralReferrer(false);
    } else if (checkReferralCode(ReferrerCodes.LguPass)) {
      setIsKakaoAvailable(false);
      setIsPassAvailable(true);
      setIsTossAvailable(false);
      setIsGeneralReferrer(false);
    } else if (checkReferralCode(ReferrerCodes.Toss)) {
      setIsKakaoAvailable(true);
      setIsPassAvailable(true);
      setIsTossAvailable(true);
      setIsGeneralReferrer(true);
    } else {
      setIsKakaoAvailable(true);
      setIsPassAvailable(true);
      setIsTossAvailable(true);
      setIsGeneralReferrer(true);
    }
    setReferrerCode(getSavedReferralCode());
  }, [userInfo?.referrer_code]);

  useEffect(() => {
    checkReferrer();
  }, [checkReferrer]);

  return {
    referrerCode,
    isKakaoAvailable,
    isPassAvailable,
    isTossAvailable,
    isGeneralReferrer,
    checkReferrer,
  };
};

export default useReferrer;

/**
 * Local storage 에 referral code를 저정
 */
export const saveReferralCode = (referralCode) => {
  localStorage.setItem(localReferralKey, referralCode);
  localStorage.setItem(localReferralExpiryKey, new Date().getTime());
};

/**
 * Local storage 에 저정된 referral code를 리턴
 */
export const getSavedReferralCode = () => {
  const referralCode = localStorage.getItem(localReferralKey);
  return referralCode || "";
};

/**
 * Local storage 에 저정된 referrer code가 입력값과 일치하는지 확인
 */
export const checkReferralCode = (inputCode) => {
  const referralCode = localStorage.getItem(localReferralKey);
  // Referrer 페이지로 들어온 경우 (회원가입 전) localstorage 에 코드를 임시 저장해둠
  // Referral 코드 유지시간 30분 적용
  const expiryTime = localStorage.getItem(localReferralExpiryKey) || 0;
  const currentDate = new Date();
  const isExpired = currentDate.getTime() > expiryTime + 1000 * 60 * 30;
  return referralCode === inputCode && !isExpired;
};

/**
 * Local storage 에 저정된 referral code가 TOSS 인지 확인
 */
export const checkTossReferralCode = () => {
  const referralCode = localStorage.getItem(localReferralKey);
  // Referral 코드 유지시간 30분 적용
  const expiryTime = localStorage.getItem(localReferralExpiryKey) || 0;
  const currentDate = new Date();
  const isExpired = currentDate.getTime() > expiryTime + 1000 * 60 * 30;
  return (
    (referralCode?.includes(ReferrerCodes.KtPass) ||
      referralCode?.includes(ReferrerCodes.LguPass)) &&
    !isExpired
  );
};

/**
 * referrer code가 KT/LG pass 인지 확인
 */
export const isKtLguReferrerCode = (referrer_code = "") => {
  return (
    referrer_code?.toLowerCase().includes(ReferrerCodes.KtPass) ||
    referrer_code?.toLowerCase().includes(ReferrerCodes.LguPass)
  );
};

/**
 * referrer code 가 버즈빌 코드인지 확인
 * @param {*} referrer_code
 * @returns
 */
export const isBuzzvilReferrerCode = (referrer_code = "") => {
  return referrer_code?.toLowerCase() === ReferrerCodes.Buzzvil;
};

/**
 * referrer code가 TOSS 인지 확인
 */
export const checkUserInfoReferrerCode = (referrer_code = "") => {
  return referrer_code?.toLowerCase() === ReferrerCodes.Toss;
};

/**
 * referrer code가 유효한지 서버에서 확인
 */
export const validateReferrerCodeFromServer = async (
  referral_code,
  saveIfValid = true,
) => {
  try {
    const res = await BaseApiService.CheckReferralCode({ referral_code });
    if (res.status === "success") {
      if (saveIfValid) {
        saveReferralCode(referral_code);
      }
    }
    return res;
  } catch (error) {
    logger("checkReferrerCodeCodeFromServer error: ", error);
  }
  return {};
};

/**
 * Local storage 에 NBT - click_key 저장
 */
export const saveNBTClickKey = (clickKey) => {
  localStorage.setItem(AppStorageKey.partner.nbtClickKey, clickKey);
};

/**
 * Local storage 에 저정된 NBT - click_key를 리턴
 */
export const getSavedNBTClickKey = () => {
  const clickKey = localStorage.getItem(AppStorageKey.partner.nbtClickKey);
  return clickKey || "";
};

/**
 * Local storage: NBT - click_key 삭제
 */
export const removeNBTClickKey = () => {
  localStorage.removeItem(AppStorageKey.partner.nbtClickKey);
};
